import type { DecoderStatsObservation } from '../guestStreamStatsReader';
import type { FramePresentationEvidence } from '../streamRecovery/contracts';

/** Producer timestamps establish identity; the local monotonic clock only ages evidence. */
export class DecoderObservationTracker {
  private baseline?: DecoderStatsObservation;
  private latest?: { framesDecoded: number; sequence: number; observedAtMs: number };
  private sequence = 0;

  constructor(private readonly deps: { nowMs: () => number }) {}

  observe(sample: DecoderStatsObservation | undefined): void {
    const now = this.deps.nowMs();
    if (!sample || !sample.sourceId || !Number.isFinite(sample.timestampMs) || sample.timestampMs < 0 ||
        !Number.isSafeInteger(sample.framesDecoded) || sample.framesDecoded < 0 || !Number.isFinite(now)) {
      this.reset();
      return;
    }
    const previous = this.baseline;
    if (!previous || previous.sourceId !== sample.sourceId || sample.timestampMs < previous.timestampMs ||
        sample.framesDecoded < previous.framesDecoded || (this.latest && now < this.latest.observedAtMs)) {
      this.reset();
      this.baseline = { ...sample };
      return;
    }
    if (sample.timestampMs === previous.timestampMs) return;
    this.baseline = { ...sample };
    this.latest = { framesDecoded: sample.framesDecoded, sequence: ++this.sequence, observedAtMs: now };
  }

  snapshot(): Pick<FramePresentationEvidence, 'decodedFrames' | 'decodeObservation'> {
    if (!this.latest) return {};
    const ageMs = this.deps.nowMs() - this.latest.observedAtMs;
    if (!Number.isFinite(ageMs) || ageMs < 0) { this.reset(); return {}; }
    return { decodedFrames: this.latest.framesDecoded,
      decodeObservation: { sequence: this.latest.sequence, ageMs } };
  }

  reset(): void { this.baseline = undefined; this.latest = undefined; this.sequence = 0; }
}
